import {createWebHistory, createRouter} from 'vue-router'
import {useAuth} from '@/stores/auth'
import {useMain} from "@/stores/main";

import PublicRoutes from './public'
import AuthRoutes from './auth'
import RegisterRoutes from './register'
import CommonRoutes from './common'
import AdminRoutes from './admin'
import OrganizationsRoutes from './organizations'
import TraineesRoutes from './trainees'
import SettingsRoutes from './settings'


const PageNotFound = () => import('@/pages/errors/404.vue')
const Dashboard = () => import('@/pages/Dashboard.vue')

// const Trainees = () => import('@/pages/Trainees.vue')

const APP_NAME = import.meta.env.VITE_APP_NAME

const routes = [
    {
        path: '/',
        component: Dashboard,
        name: 'home',
        query: {
            verified: 'verified',
        },
        meta: {
            guard: 'auth',
        },
    },

    ...PublicRoutes,
    ...RegisterRoutes,
    ...AuthRoutes,
    ...CommonRoutes,
    ...AdminRoutes,
    ...OrganizationsRoutes,
    ...TraineesRoutes,
    ...SettingsRoutes,

    /*{
        path: '/stagiaires/:token',
        name: 'trainees-token',
        component: Trainees,
        meta: {
            title: 'Stagiaires',
            guard: 'auth',
        },
    },*/

    {path: '/:pathMatch(.*)*', name: 'NotFound', component: PageNotFound},
]

const router = createRouter({
    history: createWebHistory(),
    routes,
})

// Only allow redirects to relative paths within the same origin.
// Rejects schemes, protocol-relative URLs (//evil.com) and absolute URLs.
export const safeRedirectPath = (encoded) => {
    try {
        const decoded = atob(encoded)
        if (!decoded.startsWith('/') || decoded.startsWith('//')) {
            return null
        }
        return decoded
    } catch {
        return null
    }
}

// Pure authentication/authorization dispatcher for router.beforeEach.
// Returns either a navigation target (object or string) or null when the
// navigation should proceed untouched. Pure — no router side effects.
export const resolveAuthGuard = (to, store, {externalRedirect} = {}) => {
    const auth = store.authenticated
    const verified = store.hasVerified
    const activated = store.isActive
    const isSubscribed = store.isSubscribed

    const matches = (guard) => to.matched.some((route) => route.meta.guard === guard)

    if (matches('guest') && auth) {
        const safe = to.query.redirect_to ? safeRedirectPath(to.query.redirect_to) : null
        if (safe) {
            externalRedirect?.(safe)
            return null
        }
        return {name: isSubscribed ? 'home' : 'register'}
    }

    if (matches('guest-auth') && auth && isSubscribed) {
        const safe = to.query.redirect_to ? safeRedirectPath(to.query.redirect_to) : null
        if (safe) {
            externalRedirect?.(safe)
            return null
        }
        return {name: 'home'}
    }

    if (matches('auth') && !auth) {
        const redirect = {name: 'login'}
        if (to.name !== 'home') {
            redirect.query = {redirect_to: btoa(to.fullPath)}
        }
        return redirect
    }

    if (matches('auth') && auth && !isSubscribed) {
        const redirect = {name: 'login'}
        if (to.name !== 'home') {
            redirect.query = {redirect_to: btoa(to.fullPath)}
        }
        return redirect
    }

    if (
        to.name !== 'account-verify'
        && isSubscribed
        && matches('auth')
        && (!verified || !activated)
    ) {
        return {name: 'account-verify'}
    }

    return null
}

// Navigation guard
router.beforeEach((to) => {
    return resolveAuthGuard(to, useAuth(), {
        externalRedirect: (url) => {
            window.location.href = url
        },
    }) ?? undefined
})

router.beforeEach(() => {
    useMain().routeLoading = true
})
router.afterEach(() => {
    const mainStore = useMain()
    mainStore.routeLoading = false
    setTimeout(() => mainStore.routeLoading = false, 1500)
})


// Page Title and Metadata
router.beforeEach((to) => {
    const nearestWithTitle = to.matched
        .slice()
        .reverse()
        .find(r => r.meta && r.meta.title)

    const nearestWithMeta = to.matched
        .slice()
        .reverse()
        .find(r => r.meta && r.meta.metaTags)

    if (nearestWithTitle) {
        document.title = nearestWithTitle.meta.title + ' - ' + APP_NAME
    } else {
        document.title = APP_NAME
    }

    Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map(
        el => el.parentNode.removeChild(el),
    )

    if (!nearestWithMeta) return

    nearestWithMeta.meta.metaTags
        .map(tagDef => {
            const tag = document.createElement('meta')

            Object.keys(tagDef).forEach(key => {
                tag.setAttribute(key, tagDef[key])
            })

            tag.setAttribute('data-vue-router-controlled', '')

            return tag
        })

        .forEach(tag => document.head.appendChild(tag))
})

export default router
